'use client';

import { motion } from 'framer-motion';

const AppDownload = () => {
    return (
        <section className="py-24 bg-white overflow-hidden">
            <div className="container-custom">
                <div className="bg-slate-50 rounded-[3rem] p-8 md:p-16 lg:p-20 relative flex flex-col lg:flex-row items-center gap-16">
                    {/* Content */}
                    <div className="flex-1 text-center lg:text-left z-10">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6 leading-tight">
                                Find Your Dream Home <br />
                                <span className="text-violet-600">On The Go.</span>
                            </h2>
                            <p className="text-lg text-slate-600 mb-10 max-w-lg mx-auto lg:mx-0">
                                Download the DreamAcres app for the best experience. Get real-time notifications, save your favorite properties, and chat directly with owners.
                            </p>

                            {/* Download Buttons */}
                            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 mb-12">
                                <a href="#" className="flex items-center gap-3 px-6 py-3 bg-black text-white rounded-2xl hover:bg-slate-800 transition-all duration-300 hover:scale-105 active:scale-95">
                                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M17.5 12.5c0-2.8 1.9-4.2 2-4.2-1.3-1.9-3.3-2.1-4-2.2-1.7-.2-3.3 1-4.2 1-.9 0-2.2-1-3.6-1-1.9 0-3.6 1.1-4.6 2.8C1.1 12.3 2.5 17.6 4.4 20.3c1 1.4 2.1 2.9 3.6 2.9 1.4 0 2-1 3.7-1 1.7 0 2.2 1 3.7 1 1.5 0 2.6-1.5 3.6-2.9 1.1-1.6 1.6-3.1 1.6-3.2-.1 0-2.1-.8-2.1-3.6zM15 4c.7-.9 1.2-2.1 1.1-3.3-1.1 0-2.4.7-3.2 1.6-.7.8-1.3 2-1.2 3.1 1.2.1 2.5-.6 3.3-1.4z" />
                                    </svg>
                                    <div className="text-left">
                                        <p className="text-[10px] uppercase font-bold opacity-70 leading-none mb-1">Download on</p>
                                        <p className="text-xl font-bold leading-tight">App Store</p>
                                    </div>
                                </a>
                                <a href="#" className="flex items-center gap-3 px-6 py-3 bg-black text-white rounded-2xl hover:bg-slate-800 transition-all duration-300 hover:scale-105 active:scale-95">
                                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L18.67,16.2L5.21,23.11L14.45,13.88L16.81,15.12M14.45,10.12L5.21,0.89L18.67,7.8L16.81,8.88L14.45,10.12M15.19,10.88L17.5,12L15.19,13.12L14.45,12.38L14.45,11.62L15.19,10.88Z" />
                                    </svg>
                                    <div className="text-left">
                                        <p className="text-[10px] uppercase font-bold opacity-70 leading-none mb-1">Get it on</p>
                                        <p className="text-xl font-bold leading-tight">Google Play</p>
                                    </div>
                                </a>
                            </div>

                            {/* QR Code Placeholder */}
                            <div className="flex items-center justify-center lg:justify-start gap-6 pt-6 border-t border-slate-200">
                                <div className="w-20 h-20 bg-white p-2 rounded-xl shadow-sm border border-slate-100 flex items-center justify-center">
                                    <div className="w-full h-full bg-slate-100 rounded-lg flex items-center justify-center text-slate-300">
                                        <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                        </svg>
                                    </div>
                                </div>
                                <div>
                                    <p className="font-bold text-slate-900">Scan to Download</p>
                                    <p className="text-sm text-slate-500">Available on iOS & Android</p>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* App Mockup Image */}
                    <div className="flex-1 relative w-full max-w-md lg:max-w-none">
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="relative"
                        >
                            {/* Decorative background shape */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-violet-600/10 rounded-full blur-[80px]" />

                            <img
                                src="/app_mockup.png"
                                alt="DreamAcres Mobile App"
                                className="relative z-10 w-full h-auto drop-shadow-2xl hover:scale-105 transition-transform duration-700"
                            />
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AppDownload;
